<template>
    <div class="quotation-attached-page print-content">
        <div class="quotation-attached-steps d-print-none">
            <StepItem2 :active-index="2" :deluxe="true"></StepItem2>
        </div>

        <div class="deluxe-project-back quotation-attached-back d-print-none">
            <router-link :to="BackLink">
                <i class="fa-solid fa-chevron-left"></i> {{$t('Back To Quotation')}}
            </router-link>
        </div>

        <div class="quotation-attached-header d-print-none">
            <div class="quotation-attached-heading">
                <span class="quotation-attached-eyebrow">{{ $t('View Attached Bill of Quantities') }}</span>
                <h1 class="deluxe-page-title">{{ QuotationDisplayTitle }}</h1>
            </div>
            <button class="quotation-attached-action quotation-attached-action--print" :disabled="!isLoaded" @click="PrintPage">
                <span>{{$t('Print')}}</span>
                <i class="fa-solid fa-print"></i>
            </button>
        </div>

        <FigureCurrencyNote class="quotation-attached-currency-note d-print-none" :currency="projectInfo.currencyId" />

        <section class="quotation-attached-card">
            <div class="quotation-attached-report-head">
                <div class="quotation-attached-report-address">
                    <span class="quotation-attached-report-label">{{ $t('To') }} ({{ $t('Client') }})</span>
                    <h2>{{ ClientCompany }}</h2>
                    <p class="quotation-attached-address-line">
                        <span>{{ $t('Address') }}:</span>
                        <strong style="white-space: pre-line">{{ quotationData.address || '-' }}</strong>
                    </p>
                    <div class="quotation-attached-report-meta">
                        <p>
                            <span>{{ $t('Attention') }}:</span>
                            <strong class="text-capitalize">{{ quotationData.attentionClient?.userId?.fullname || '-' }}</strong>
                        </p>
                        <p class="quotation-attached-report-project">
                            <strong>{{ quotationData.quotationName || '-' }}</strong>
                        </p>
                        <p>
                            <span>{{ $t('Layout Date based') }}:</span>
                            <strong>{{ FormatLayoutDate }}</strong>
                        </p>
                        <p>
                            <span>{{ $t('Quotation Module.RE') }}:</span>
                            <strong>{{ quotationData.remark || 'N/A' }}</strong>
                        </p>
                    </div>
                </div>
                <div class="quotation-attached-report-number">
                    <p>{{ $t('Quotation No') }}</p>
                    <h4>{{ quotationData.quotationNumber || '-' }}</h4>
                </div>
            </div>

            <div class="quotation-attached-table-wrap">
                <table class="quotation-attached-table">
                    <thead>
                        <tr>
                            <th class="quotation-attached-sn-col">{{$t('S/N')}}</th>
                            <th class="quotation-attached-description-col">{{$t('Description')}}</th>
                            <th>{{$t('Unit')}}</th>
                            <th>{{$t('Space ID')}}</th>
                            <th class="text-end">{{$t('Quantity')}}</th>
                            <th class="text-end">{{$t('Rate')}}({{projectInfo.currencyId?.sign}})</th>
                            <th class="text-end">{{$t('Total')}}({{projectInfo.currencyId?.sign}})</th>
                        </tr>
                    </thead>
                    <tbody v-if="!isLoaded">
                        <tr>
                            <td class="text-center" colspan="7">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">{{$t('Loading')}}...</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-if="boqSections.length==0">
                            <td class="text-center" colspan="7">{{$t('No data Found')}}</td>
                        </tr>
                        <template v-for="(item,key) in boqSections" :key="key">
                            <tr class="quotation-attached-reference-row">
                                <td></td>
                                <td class="fw-bold text-capitalize">{{ item.name }}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            <BoqCreatorPrintViewRow :sections="item.data.sections" :IsOpenBook="false" />
                            <tr v-for="(lineItem,lineItemkey) in item.lineItems" :key="lineItemkey" class="quotation-attached-line-row">
                                <td class="text-center">
                                    {{ lineItemkey + 1 }}
                                </td>
                                <td>
                                    <div class="quotation-attached-line-copy">
                                        <small class="fw-bold text-capitalize" v-if="userInfo.language=='en'">{{lineItem.name}}</small>
                                        <small class="fw-bold" v-else>{{lineItem.nameCh}}</small>
                                        <p class="small mb-0 description-line-item">
                                            {{lineItem.description}}
                                        </p>
                                    </div>
                                </td>
                                <td>{{lineItem.unit}}</td>
                                <td>{{lineItem.area}}</td>
                                <td class="text-end">
                                    <span v-if="!lineItem.rateOnly">
                                        {{ReformatNumber(lineItem.totalQuantity,0,2)}}
                                    </span>
                                    <span class="text-danger" v-else>
                                        {{$t('RATE ONLY')}}
                                    </span>
                                </td>
                                <td class="text-end">
                                    {{ReformatNumber(lineItem.sellRate)}}
                                </td>
                                <td class="text-end">
                                    <span v-if="!lineItem.rateOnly">
                                        {{ReformatNumber(lineItem.sellTotal)}}
                                    </span>
                                    <span class="text-danger" v-else>
                                        -
                                    </span>
                                </td>
                            </tr>

                            <tr class="quotation-attached-total-row" v-if="item.data.sections.length==0">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td class="fw-bold text-capitalize text-end">{{ $t('Sub Total Of') }} {{ item.name }}</td>
                                <td class="text-end">{{ ReformatNumber(item.data.overAllTotal.sellTotal) }}</td>
                            </tr>
                        </template>
                        <tr class="quotation-attached-total-row quotation-attached-overall-row">
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td class="fw-bold text-capitalize text-end">{{ $t('Overall Total') }}</td>
                            <td class="text-end">{{ ReformatNumber(overAllTotal) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    </div>
</template>

<style scoped>
.quotation-attached-page {
    color: #1e1b16;
}

.quotation-attached-steps {
    margin-bottom: 2.65rem;
}

.quotation-attached-back {
    margin-bottom: 1.55rem;
}

.quotation-attached-back a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #777777;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
}

.quotation-attached-back a:hover,
.quotation-attached-back a:focus {
    color: var(--lens-accent);
}

.quotation-attached-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 2rem;
    margin-bottom: 1.35rem;
}

.quotation-attached-heading {
    min-width: 0;
}

.quotation-attached-eyebrow {
    display: block;
    margin-bottom: 0.45rem;
    color: #1e1b16;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.quotation-attached-heading .deluxe-page-title {
    margin: 0;
    color: var(--lens-accent);
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.15;
}

.quotation-attached-action {
    display: inline-flex;
    min-height: 2.8rem;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    border: 0;
    padding: 0.75rem 1.55rem;
    color: #ffffff;
    background: #07131b;
    font-size: 0.88rem;
    font-weight: 700;
    white-space: nowrap;
}

.quotation-attached-action:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.quotation-attached-currency-note {
    display: block;
    margin: 0.25rem 0 1.55rem;
    color: #777777;
    font-size: 0.865rem;
    font-style: italic;
    text-align: center;
}

.quotation-attached-card {
    width: 100%;
    min-height: 80vh;
    padding: 2.5rem;
    background: #ffffff;
    box-shadow: 0 0.1rem 0.35rem rgba(30, 27, 22, 0.08);
}

.quotation-attached-report-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 3rem;
    margin-bottom: 2.4rem;
}

.quotation-attached-report-address {
    display: grid;
    grid-template-columns: minmax(0, 25rem) minmax(0, 1fr);
    gap: 0.25rem 6rem;
    max-width: 68rem;
}

.quotation-attached-report-label,
.quotation-attached-report-address h2,
.quotation-attached-address-line {
    grid-column: 1;
}

.quotation-attached-report-meta {
    grid-column: 2;
    grid-row: 2 / span 3;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding-top: 1rem;
}

.quotation-attached-report-label {
    color: #1e1b16;
    font-size: 0.875rem;
    font-weight: 600;
}

.quotation-attached-report-address h2 {
    margin: 0.9rem 0 1.35rem;
    color: #1e1b16;
    font-size: 1rem;
    font-weight: 800;
}

.quotation-attached-report-address p,
.quotation-attached-report-meta p,
.quotation-attached-report-number p {
    margin: 0;
    color: #1e1b16;
    font-size: 0.875rem;
    line-height: 1.35;
}

.quotation-attached-report-address span,
.quotation-attached-report-meta span {
    margin-right: 0.25rem;
    color: #1e1b16;
    font-weight: 600;
}

.quotation-attached-report-address strong,
.quotation-attached-report-meta strong {
    font-weight: 500;
}

.quotation-attached-report-project strong {
    font-weight: 700;
}

.quotation-attached-report-number {
    min-width: 13rem;
    text-align: right;
}

.quotation-attached-report-number h4 {
    margin: 0.35rem 0 0.5rem;
    color: #1e1b16;
    font-size: 1.15rem;
    font-weight: 800;
}

.quotation-attached-table-wrap {
    width: 100%;
    overflow-x: visible;
}

.quotation-attached-table {
    width: 100%;
    margin-bottom: 0;
    border: 0;
    border-collapse: collapse;
    color: #1e1b16;
    background: #ffffff;
    table-layout: auto;
    font-size: 0.875rem;
}

.quotation-attached-table th {
    border: 0;
    border-bottom: 1px solid var(--lens-accent);
    padding: 0.78rem 1.45rem;
    color: var(--lens-accent);
    background: color-mix(in srgb, var(--lens-accent) 18%, #ffffff);
    font-size: 0.86rem;
    font-weight: 800;
    line-height: 1.25;
    text-transform: none;
    white-space: nowrap;
}

.quotation-attached-table :deep(td) {
    border: 0;
    border-bottom: 1px solid #d8d8d8;
    padding: 0.9rem 1.45rem;
    background: #ffffff;
    vertical-align: middle;
}

.quotation-attached-table .quotation-attached-sn-col,
.quotation-attached-table :deep(td:first-child) {
    width: 5rem;
    text-align: center;
}

.quotation-attached-description-col,
.quotation-attached-table :deep(td:nth-child(2)) {
    width: auto;
    min-width: 20rem;
}

.quotation-attached-table th:nth-child(3),
.quotation-attached-table :deep(td:nth-child(3)) {
    width: 6rem;
}

.quotation-attached-table th:nth-child(4),
.quotation-attached-table :deep(td:nth-child(4)) {
    width: 7rem;
}

.quotation-attached-table th:nth-child(5),
.quotation-attached-table :deep(td:nth-child(5)),
.quotation-attached-table th:nth-child(6),
.quotation-attached-table :deep(td:nth-child(6)),
.quotation-attached-table th:nth-child(7),
.quotation-attached-table :deep(td:nth-child(7)) {
    width: 10rem;
}

.quotation-attached-reference-row td,
.quotation-attached-table :deep(tr.bg-light td),
.quotation-attached-table :deep(tr.bg-light > *) {
    background: #e7e8eb !important;
    color: #1e1b16;
    font-weight: 700 !important;
}

.quotation-attached-line-copy {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding-left: 1.35rem;
}

.quotation-attached-line-copy small,
.quotation-attached-table :deep(td small.fw-bold) {
    color: #002776;
    font-size: 0.82rem;
}

.quotation-attached-line-copy p,
.quotation-attached-table :deep(.description-line-item) {
    color: #1e1b16;
    font-size: 0.78rem;
    line-height: 1.35;
}

.quotation-attached-table :deep(.ps-3) {
    padding-left: 1.5rem !important;
}

.quotation-attached-table :deep(.ps-4) {
    padding-left: 2.25rem !important;
}

.quotation-attached-table :deep(.ps-5) {
    padding-left: 3rem !important;
}

.quotation-attached-total-row td,
.quotation-attached-table :deep(tr.bg-light-primary td),
.quotation-attached-table :deep(tr.bg-light-primary > *) {
    background: color-mix(in srgb, var(--lens-accent) 10%, #ffffff) !important;
    font-weight: 800 !important;
}

.quotation-attached-overall-row td {
    border-top: 1px solid var(--lens-accent);
}

@media (max-width: 992px) {
    .quotation-attached-header,
    .quotation-attached-report-head {
        flex-direction: column;
    }

    .quotation-attached-report-number {
        text-align: left;
    }

    .quotation-attached-report-address {
        grid-template-columns: 1fr;
    }

    .quotation-attached-report-meta {
        grid-column: 1;
        grid-row: auto;
        padding-top: 0;
    }

    .quotation-attached-card {
        padding: 1.4rem;
    }

    .quotation-attached-table-wrap {
        overflow-x: auto;
    }

    .quotation-attached-table {
        min-width: 58rem;
    }
}

@page {
    size: A4 landscape;
    margin: 8mm;
}

@media print {
    :global(html),
    :global(body),
    :global(#app),
    :global(.deluxe-layout),
    :global(.deluxe-layout .wrapper),
    :global(.deluxe-layout .wrapper__inner),
    :global(.deluxe-layout .content),
    :global(.deluxe-layout .content__wrapper),
    :global(.deluxe-layout .content__inner) {
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
    }

    :global(.deluxe-layout .wrapper__inner),
    :global(.deluxe-layout .content) {
        display: block !important;
    }

    :global(.deluxe-layout .content) {
        flex: none !important;
    }

    :global(.deluxe-layout .content__wrapper) {
        padding: 0 !important;
    }

    :global(.deluxe-layout .deluxe-sidebar),
    :global(.deluxe-layout .deluxe-sidebar__collapse),
    :global(.deluxe-layout .content__top) {
        display: none !important;
    }

    .quotation-attached-page {
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        color: #000000;
        background: #ffffff;
    }

    .quotation-attached-card {
        width: 100% !important;
        height: auto !important;
        min-height: 0;
        max-height: none !important;
        overflow: visible !important;
        padding: 0 !important;
        background: #ffffff !important;
        box-shadow: none !important;
    }

    .quotation-attached-report-head {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        gap: 1.5rem !important;
        margin-bottom: 1.25rem !important;
        page-break-inside: avoid;
    }

    .quotation-attached-report-address {
        display: grid !important;
        grid-template-columns: minmax(0, 52%) minmax(0, 38%) !important;
        gap: 0.15rem 2rem !important;
        max-width: none !important;
        width: 72% !important;
    }

    .quotation-attached-report-meta {
        grid-column: 2 !important;
        grid-row: 2 / span 3 !important;
        padding-top: 0.9rem !important;
    }

    .quotation-attached-report-number {
        min-width: 8rem !important;
        text-align: right !important;
    }

    .quotation-attached-report-address p,
    .quotation-attached-report-meta p,
    .quotation-attached-report-number p {
        font-size: 8px !important;
        line-height: 1.25 !important;
    }

    .quotation-attached-report-address h2 {
        margin: 0.45rem 0 0.85rem !important;
        font-size: 9px !important;
    }

    .quotation-attached-report-number h4 {
        font-size: 10px !important;
    }

    .quotation-attached-currency-note {
        margin: 0 0 0.75rem !important;
        font-size: 8px !important;
        text-align: center !important;
    }

    .quotation-attached-table-wrap {
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
    }

    .print-content {
        font-size : 10px !important;
    }
    .print-body {
        margin-top : 190px;
    }
    .signature-padding{
        margin-top:50px
    }
    :global(body) {
        margin-top: 0.1mm;
        margin-bottom: 0.15mm;
        margin-right: 0.30mm;
        margin-left: 0.30mm;
    }

    .quotation-attached-table {
        width: 100% !important;
        height: auto !important;
        min-width: 0 !important;
        table-layout: fixed !important;
        font-size: 7px !important;
        page-break-inside: auto;
    }

    .quotation-attached-table thead {
        display: table-row-group;
    }

    .quotation-attached-table tbody {
        display: table-row-group;
    }

    .quotation-attached-table th,
    .quotation-attached-table :deep(td) {
        padding: 0.18rem 0.22rem !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
    }

    .quotation-attached-table .quotation-attached-sn-col,
    .quotation-attached-table :deep(td:first-child) {
        width: 5% !important;
    }

    .quotation-attached-description-col,
    .quotation-attached-table :deep(td:nth-child(2)) {
        width: 38% !important;
        min-width: 0 !important;
    }

    .quotation-attached-table th:nth-child(3),
    .quotation-attached-table :deep(td:nth-child(3)) {
        width: 7% !important;
    }

    .quotation-attached-table th:nth-child(4),
    .quotation-attached-table :deep(td:nth-child(4)) {
        width: 8% !important;
    }

    .quotation-attached-table th:nth-child(5),
    .quotation-attached-table :deep(td:nth-child(5)) {
        width: 10% !important;
    }

    .quotation-attached-table th:nth-child(6),
    .quotation-attached-table :deep(td:nth-child(6)),
    .quotation-attached-table th:nth-child(7),
    .quotation-attached-table :deep(td:nth-child(7)) {
        width: 16% !important;
    }

    .quotation-attached-line-copy {
        padding-left: 0.25rem !important;
    }

    .quotation-attached-line-copy small,
    .quotation-attached-table :deep(td small.fw-bold),
    .quotation-attached-line-copy p,
    .quotation-attached-table :deep(.description-line-item) {
        font-size: 7px !important;
        line-height: 1.2 !important;
    }

    .quotation-attached-table :deep(.ps-3),
    .quotation-attached-table :deep(.ps-4),
    .quotation-attached-table :deep(.ps-5) {
        padding-left: 0.75rem !important;
    }

    .quotation-attached-table tr {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .bg-light{
        padding-top: 0px;
        padding-bottom: 0px;
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
    }

    .bg-separator{
        font-weight: 600;
        padding-top  : 15px;
        background-color: #ffc000;
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
        border-bottom: 1px solid #000;
    }

    .min-width-100{
        min-width: 100px;
    }
}
</style>

<script>
import {LinkList, ReformatNumber, ReformatDate} from '@/helpers/utilities.js'
import {mapActions, mapGetters} from 'vuex'
import {GetQuotationOne,GetQuotationOneLineItemsByBits, GetQuotationOneLineItemsSections} from '@/actions/boqActions.js'
import BoqCreatorPrintViewRow from '@/components/boq/BoqCreatorPrintViewRow.vue'
import FigureCurrencyNote from '@/components/layout/FigureCurrencyNote.vue'
import StepItem2 from '@/components/layout/StepItem2.vue'
import 'dotenv/config'
export default {
    name : 'BoqQuotationAttachedItems',
    components : {
        BoqCreatorPrintViewRow, FigureCurrencyNote, StepItem2
    },
    mounted : async function(){

        const self = this
        self.ChangeActiveLink(LinkList.QUOTATIONS)

        const quotationId = self.$route.params.id
        const  quotationData = await GetQuotationOne(quotationId)
        if(quotationData=='401'){
            return self.$router.push('/unauthorized')
        }
        await self.GetProjectInfo(quotationData.projectId._id)

        const revisionId = self.$route.query.revisionId
        if(revisionId){
            self.lineItemParams.showLive = false
            self.lineItemParams.revisionId = revisionId

            self.quotationData = quotationData.revisionList.find(u=>u._id==revisionId)
        }else{
            self.quotationData = quotationData
        }
        await self.GetDataByBits()
        self.boqSections =  await GetQuotationOneLineItemsSections({
            id : self.$route.params.id,
            revisionId :  this.$route.query.revisionId
        })
        let overAllTotal = 0

        const findTheChild = (childs, boqId) =>{
            for(let i = 0; i < childs.length; i++){
                const getChild = childs[i]
                if(getChild.subTotal?.sellTotal!=0 && getChild.level!=1){
                    getChild.lineItems = self.lineItems.filter(u=>u.boqId==boqId && u.boqSequenceParentId==getChild.id)
                }

                if(getChild.childs.length!=0){
                    findTheChild(getChild.childs,boqId)
                }
                
            }
        }
        //add the line items
        for(let x =0; x<self.boqSections.length;x++)
        {
            const boq = self.boqSections[x]
            if(boq.data.sections.length==0){
                boq.lineItems = self.lineItems.filter(u=>u.boqId==boq._id)
            }
            else{
                findTheChild(boq.data.sections,boq._id)
            }
            overAllTotal += boq.data.overAllTotal.sellTotal
        }

        self.overAllTotal = overAllTotal

    },
    methods : {
        ...mapActions(['ChangeActiveLink', 'GetProjectInfo']),
        ReformatNumber :ReformatNumber,
        ReformatDate :ReformatDate,
        async GetDataByBits(){
            const self = this

            self.lineItems = []
            let willFetch = true
            self.lineItemParams.skip = 0
            self.isLoaded = false
            while(willFetch){
                const bits = await GetQuotationOneLineItemsByBits(self.lineItemParams)
                if(bits.length!=0){
                    self.lineItems.push(...bits.map(self.NormaliseLineItem))
                }
                if(bits.length==self.lineItemParams.take){
                    self.lineItemParams.skip += 50
                }else{
                    willFetch = false
                }
            }

            self.isLoaded = true
        },
        NormaliseLineItem(item){
            return {
                ...item,
                remark : item.remark || 'Included'
            }
        },
        PrintPage(){
            window.print()
        }
    },
    computed :{
        ...mapGetters(['projectInfo','permission','userInfo']),
        BackLink(){
            return this.returnUrl || { name : 'BoqQuotation', params : { id : this.quotationId } }
        },
        QuotationDisplayTitle(){
            const number = this.quotationData.quotationNumber || ''
            const name = this.quotationData.quotationName || ''
            return `${number} ${name}`.trim() || this.$t('Quotation')
        },
        ClientCompany(){
            return this.quotationData.attentionClient?.userId?.company || '-'
        },
        FormatLayoutDate(){
            const self = this
            if(self.quotationData.layoutDate){
                return ReformatDate(new Date(self.quotationData.layoutDate),"Do of MMMM, YYYY")
            }else{
                return '-'
            }
        },
        LineItemList(){
            const self = this 
            if(self.lineItems.length==0){
                 return []
            }

            let parentLetter = 65
            let subCount = 1
            let lineItemSubCount = 1
      
            let mainCateg = { costCodeId : '' }
            let subCateg = { costCodeId : '' }
      
            let fieldListMain = {
                 sellTotal : 0,
                 index : 0
             }
      
             let fieldListSub = {
                sellTotal : 0,
                index : 0
             }
      
             let totalList= []
             let totalListSub= []
      
            const arrayLength = self.lineItems.length
            let ReformatData = self.lineItems.map((u,index)=>{
                 let showMainCateg = false
                 let showSubCatag = false
                 const nextLineItem = self.lineItems[index + 1]
                 if(u.rateOnly!=true){
                    //main category sum 
                    fieldListMain.sellTotal += u.sellTotal
                    //sub category sum
                    fieldListSub.sellTotal += u.sellTotal
                 }
      
      
                 if(u.parents[0].costCodeId!=mainCateg.costCodeId){
                    showMainCateg = true
                    mainCateg = u.parents[0]
                    mainCateg.label  = String.fromCharCode(parentLetter++)
                    subCount = 1
                    fieldListMain.index = index
                 }
             
                 if(u.parents[1].costCodeId!=subCateg.costCodeId){
                    showSubCatag = true
                    subCateg = u.parents[1]
                    subCateg.label  = mainCateg.label + (subCount++)
                    fieldListSub.index = index
                    lineItemSubCount =  1
                 }
             
                 //main category
                 if(index + 1==arrayLength){
                    totalList.push({
                        sellTotal : fieldListMain.sellTotal  ,
                        index : fieldListMain.index,
                    })
                }else if(u.parents[0].costCodeId!=nextLineItem.parents[0].costCodeId){
                    totalList.push({
                        sellTotal : fieldListMain.sellTotal  ,
                        index : fieldListMain.index,
                    })
 
                    fieldListMain = {
                        sellTotal : 0  ,
                        index : 0,
                    }
                }
                //sub category
                if(index + 1==arrayLength){
                    totalListSub.push({
                        sellTotal : fieldListSub.sellTotal  ,
                        index : fieldListSub.index,
                    })
                }else if(u.parents[1].costCodeId!=nextLineItem.parents[1].costCodeId){
                    totalListSub.push({
                        sellTotal : fieldListSub.sellTotal  ,
                        index : fieldListSub.index,
                    })
 
                    fieldListSub = {
                        sellTotal : 0  ,
                        index : 0,
                    }
                }
                u.label = subCateg.label + '.' + (lineItemSubCount++)
                u.mainCateg = showMainCateg ? mainCateg : null
                u.subCateg = showSubCatag ? subCateg : null
                return u
            })
            //loop main totals
            for(let x=0;x<totalList.length;x++){
                const data = totalList[x]
                ReformatData[data.index].mainCateg
                ReformatData[data.index].mainCateg.sellTotal = data.sellTotal  
            }
            for(let x=0;x<totalListSub.length;x++){
                const data = totalListSub[x]
                ReformatData[data.index].subCateg.sellTotal = data.sellTotal  
            }
            return ReformatData
        }
    },
    data(){
        return {
            overAllTotal : 0,
            boqSections : [],
            quotationData: {
                _id : 1,
                approvalStage : 1,
                attentionClient : {
                    userId : {fullname  :''}
                }
            },
            quotationId : this.$route.params.id,
            projectUrl : process.env.VUE_APP_PROJECT_URL,
            boqUrl : process.env.VUE_APP_BOQ_URL,
            lineItemParams : {
                id : this.$route.params.id,
                take : 50,
                skip : 0,
                showLive : this.$route.query.revisionId===undefined,
                revisionId : this.$route.query.revisionId
            },
            returnUrl :  this.$route.query.returnUrl,
            lineItems : [],
            isLoaded : false
        }
    }
}
</script>
